import React, { useState, useEffect } from 'react';
import { getAllLeads, exportLeadsToExcel, exportLeadsToPDF, deleteLead } from '../services/leadService';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { 
  Search, 
  Filter, 
  Trash2, 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Mail, 
  Phone, 
  FileSpreadsheet,
  FileText,
  Clock
} from 'lucide-react';

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeads = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllLeads();
      if (!data || !Array.isArray(data)) throw new Error('Invalid data');
      
      const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.created_at || a.timestamp || 0);
          const dateB = new Date(b.created_at || b.timestamp || 0);
          return dateB - dateA;
      });
      setLeads(sortedData);
    } catch (error) {
      console.error('Error loading leads:', error);
      setError('Failed to load leads.');
      showToast('Failed to load leads', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure? This cannot be undone.')) {
      try {
        await deleteLead(id);
        loadLeads();
        showToast('Lead deleted', 'success');
      } catch (error) {
        showToast('Failed to delete', 'error');
      }
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesFilter = filter === 'all' || lead.type === filter || lead.source === filter;
    const matchesSearch =
      !searchTerm ||
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [filter, searchTerm]);

  // ... Export functions remain the same ...
const handleExportExcel = () => {
  try {
    if (!filteredLeads || filteredLeads.length === 0) {
      showToast('No leads to export', 'error');
      return;
    }
    
    // The service now handles the entire file creation and download
    exportLeadsToExcel(filteredLeads);
    
    showToast('Excel downloaded successfully!', 'success');
  } catch (error) {
    console.error('Export failed:', error);
    showToast('Failed to export Excel', 'error');
  }
};

  const handleExportPDF = async () => {
    try {
      await exportLeadsToPDF(filteredLeads);
      showToast('PDF opened', 'success');
    } catch (error) { showToast('PDF export failed', 'error'); }
  };

  const getLeadTypeLabel = (type) => {
    const labels = {
      ielts_mock_test: { text: 'IELTS Mock', color: 'bg-purple-100 text-purple-700 border-purple-200' },
      free_counselling_study_abroad: { text: 'Counselling', color: 'bg-blue-100 text-blue-700 border-blue-200' },
      free_demo_class: { text: 'Demo Class', color: 'bg-green-100 text-green-700 border-green-200' },
      contact_inquiry: { text: 'Contact', color: 'bg-gray-100 text-gray-700 border-gray-200' },
      general_inquiry: { text: 'General', color: 'bg-slate-100 text-slate-700 border-slate-200' },
      random_popup: { text: 'Popup Lead', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    };
    return labels[type] || { text: type, color: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Lead Management</h1>
            <p className="text-slate-500 text-xs md:text-sm mt-1">View and manage student inquiries.</p>
        </div>
        {/* Mobile Optimization: Full width buttons on mobile */}
        <div className="flex gap-2 w-full md:w-auto">
          <button onClick={handleExportExcel} className="flex-1 md:flex-none justify-center flex items-center gap-2 bg-green-600 text-white px-4 py-2 text-sm rounded-lg font-medium hover:bg-green-700 shadow-sm transition-all">
            <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Export</span> Excel
          </button>
          <button onClick={handleExportPDF} className="flex-1 md:flex-none justify-center flex items-center gap-2 bg-red-600 text-white px-4 py-2 text-sm rounded-lg font-medium hover:bg-red-700 shadow-sm transition-all">
            <FileText size={16} /> <span className="hidden sm:inline">Export</span> PDF
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
            />
          </div>
          <div className="relative md:min-w-[200px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm appearance-none cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="ielts_mock_test">IELTS Mock Test</option>
              <option value="free_counselling_study_abroad">Free Counselling</option>
              <option value="free_demo_class">Free Demo Class</option>
              <option value="contact_inquiry">Contact Form</option>
              <option value="random_popup">Popup Form</option>
            </select>
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="bg-transparent md:bg-white md:rounded-xl md:border md:border-slate-200 md:shadow-sm md:overflow-hidden min-h-[400px]">
        {isLoading ? (
          <div className="p-12 text-center bg-white rounded-xl border border-slate-200 md:border-none">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
            <p className="text-slate-500 text-sm">Loading...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center bg-white rounded-xl border border-slate-200 md:border-none">
            <p className="text-red-500 mb-4">{error}</p>
            <button onClick={loadLeads} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Retry</button>
          </div>
        ) : filteredLeads.length > 0 ? (
          <>
            {/* =========================================
                DESKTOP VIEW (TABLE)
            ========================================= */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4">Candidate</th>
                    <th className="px-6 py-4">Contact Info</th>
                    <th className="px-6 py-4">Inquiry Type</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedLeads.map((lead) => {
                    const typeInfo = getLeadTypeLabel(lead.type);
                    return (
                    <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs">
                            {(lead.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900">{lead.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 text-slate-600">
                          <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" />{lead.email || '-'}</div>
                          <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" />{lead.phone || '-'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${typeInfo.color}`}>
                          {typeInfo.text}
                        </span>
                        <div className="text-xs text-slate-400 mt-1 pl-1">via {lead.source?.replace(/_/g, ' ') || 'Website'}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-500">
                        <div className="flex items-center gap-2"><Calendar size={14} />{new Date(lead.created_at || lead.timestamp).toLocaleDateString()}</div>
                        <div className="text-xs text-slate-400 pl-6">{new Date(lead.created_at || lead.timestamp).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => handleDelete(lead.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>

            {/* =========================================
                MOBILE VIEW (CARDS)
            ========================================= */}
            <div className="md:hidden flex flex-col gap-3">
              {paginatedLeads.map((lead) => {
                const typeInfo = getLeadTypeLabel(lead.type);
                return (
                  <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    {/* Header: Type + Date */}
                    <div className="flex justify-between items-start mb-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${typeInfo.color}`}>
                        {typeInfo.text}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(lead.created_at || lead.timestamp).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Body: Info */}
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-sm shrink-0">
                            {(lead.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-slate-900 truncate">{lead.name || 'Unknown'}</h3>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Mail size={12} /> <span className="truncate">{lead.email || 'No email'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Phone size={12} /> <span>{lead.phone || 'No phone'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer: Source + Actions */}
                    <div className="flex justify-between items-center pt-3 border-t border-slate-50">
                        <span className="text-xs text-slate-400 italic">via {lead.source?.replace(/_/g, ' ') || 'Website'}</span>
                        <button 
                            onClick={() => handleDelete(lead.id)} 
                            className="flex items-center gap-1 text-xs font-bold text-red-600 px-3 py-1.5 bg-red-50 rounded-lg active:bg-red-100"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="p-12 text-center bg-white rounded-xl border border-slate-200 md:border-none">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Search size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">No leads found</h3>
            <p className="text-slate-500 mt-1 text-sm">Try adjusting your filters.</p>
          </div>
        )}
        
        {/* FOOTER & PAGINATION */}
        {!isLoading && filteredLeads.length > 0 && (
          <div className="bg-white md:bg-slate-50 px-4 md:px-6 py-4 border-t border-slate-200 rounded-b-xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 md:mt-0">
            <div className="text-xs text-slate-500 font-medium text-center sm:text-left">
              Showing <span className="font-bold text-slate-700">{startIndex + 1}-{Math.min(endIndex, filteredLeads.length)}</span> of <span className="font-bold text-slate-700">{filteredLeads.length}</span>
            </div>
            
            {totalPages > 1 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center px-4 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg">
                  {currentPage} / {totalPages}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={hideToast} />}
    </div>
  );
};

export default AdminLeads;